import React, { forwardRef, HTMLAttributes, useCallback, useState, useEffect } from 'react';
import '../../base.scss';
import './Input.scss';
import { classNames } from '../../utilities';
import formatValue from './format/formatValue';
import isNumber from './helpers/isNumber';
import toNumber from './helpers/toNumber';
import unformatLocaleNumber from './helpers/unformatNumber';

export interface InputProps extends HTMLAttributes<HTMLInputElement> {
  /** required for accessibility if no labels */
  'aria-label'?: string;
  /**
   * Enable browser autocomplete
   * @default true
   */
  autoComplete?: boolean;
  /** Attach a custom class to the input container */
  className?: string;
  /** Optional currency to use with the displayFormatted prop, i.e. 'USD' */
  currency?: string;
  /** Initial value for uncontrolled inputs */
  defaultValue?: string;
  /** Disable the input */
  disabled?: boolean;
  /** Boolean error state for the input box (true means yes there is an error) */
  error?: boolean;
  /** ID for the checkbox */
  id?: string;
  /** Optional locale to use with the displayFormatted prop, i.e. 'en' or 'en-US' */
  locale?: string;
  /** Maximum Value for number or date fields */
  max?: number | string;
  /** Maximum number of decimals, used with displayFormatted */
  maxNumberOfDecimals?: number;
  /** Maximum number of characters */
  maxLength?: number;
  /** Minimum Value for number or date fields */
  min?: number | string;
  /** Minimum number of decimals, used with displayFormatted */
  minNumberOfDecimals?: number;
  /** Name to reference this component with a label */
  name?: string;
  /** Placeholder for empty input */
  placeholder?: string;
  /**
   * Input is not editable
   * @default false
   */
  readOnly?: boolean;
  /**
   * Input native type
   * @default text
   */
  /** Accessibility based role */
  role?: string;
  type?: 'text' | 'email' | 'number' | 'password' | 'tel' | 'url' | 'date' | 'hidden' | 'percent' | 'currency';
  /**
   * If true, format numbers, currencies, and percents on the UI based on currency and locale
   * but still emit the raw number underneath to use for any logic
   */
  displayFormatted?: boolean;
  /** Value for controlled inputs */
  value?: string | number;
  /** Used with displayFormatted; emit and show zero instead of undefined */
  zeroOnEmpty?: boolean;
  /** Used with displayFormatted; if true, then don't depend on parent component passing in value */
  uncontrolled?: boolean;
  /** Callback handler for value changes */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, newValue?: string | number) => void;
  /** Callback handler for on key press changes */
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Callback handler for onBlur event */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>, value?: string | number) => void;
}

// given a number (for displayFormatted cases), format based on locale
// currency, min, max, minNumberOfDecimals, and maxNumberOfDecimals
// returns a string
function format(props: InputProps, value?: number): string {
  // note: make sure it's value != null, so 0s aren't ignored
  if (
    props.displayFormatted &&
    (value != null || (props.zeroOnEmpty && value == null)) &&
    (props.type === 'number' || props.type === 'currency' || props.type === 'percent')
  ) {
    return formatValue(
      props.zeroOnEmpty && !value ? 0 : value,
      props.type,
      props.locale || '',
      props.currency || '',
      toNumber(props.min),
      toNumber(props.max),
      props.minNumberOfDecimals,
      props.maxNumberOfDecimals
    );
  }

  return '';
}

const Input = forwardRef<HTMLInputElement, InputProps>((props: InputProps, componentRef) => {
  const {
    autoComplete = true,
    className,
    defaultValue,
    disabled,
    error,
    id,
    max,
    maxLength,
    min,
    name,
    onBlur,
    onChange,
    onClick,
    onKeyDown,
    placeholder,
    readOnly,
    role,
    type = 'text',
    displayFormatted,
    value,
    zeroOnEmpty,
  } = props;

  const [formattedValue, setFormattedValue] = useState(format(props, toNumber(value)));
  const [unformattedValue, setUnformattedValue] = useState(zeroOnEmpty && !value ? '0' : value?.toString());
  const [focused, setFocused] = useState(false);
  const [lastValidValue, setLastValidValue] = useState(zeroOnEmpty && !value ? 0 : value);

  useEffect(() => {
    if (
      props.displayFormatted &&
      !props.uncontrolled &&
      (props.value == null || isNumber(props.value)) &&
      props.value !== '-' &&
      props.value !== '.' &&
      props.value !== '0.'
    ) {
      setFormattedValue(format(props, toNumber(props.value)));
      setUnformattedValue(props.zeroOnEmpty && !props.value ? '0' : props.value?.toString());
      setLastValidValue(props.zeroOnEmpty && !props.value ? 0 : props.value);
    }
  }, [props]);

  // TBD: should we destructure props with a ...others and pass that to the <input>?
  //   then we'd get a lot of useful functionality for free.
  if ((max || min) && !['number', 'date', 'currency', 'percent'].includes(type)) {
    throw new Error('Max and Min props are only valid for number, date, currency, and percent inputs.');
  }

  if (defaultValue && value) {
    throw new Error('Inputs should either be controlled (value) or uncontrolled (defaultValue).');
  }

  const onChangeWrapper = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        // if displayFormatted, set the (text) changeValue to be unformattedValue
        // and then convert/emit the number version of that value up (if valid)
        // and set the lastValidValue to the changeValue
        if (displayFormatted) {
          const changeValue = event.target.value;
          setUnformattedValue(changeValue);

          if (isNumber(changeValue) || !changeValue) {
            if (zeroOnEmpty && !changeValue) {
              setLastValidValue(0);
            } else {
              setLastValidValue(changeValue);
            }
          }

          if (changeValue !== '-' && changeValue !== '.' && changeValue !== '0.') {
            onChange(event, toNumber(changeValue));
          }
        } else {
          onChange(event, event.target.value ?? '');
        }
      }
    },
    [onChange, displayFormatted, zeroOnEmpty]
  );

  const onKeyDownWrapper = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (onKeyDown) {
        onKeyDown(event);
      }
    },
    [onKeyDown]
  );

  const onBlurWrapper = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (onBlur) {
        // if displayFormatted, check if unformattedValue is a valid number
        // and if so, emit that as the onBlur value
        // else, emit the last valid value that was set
        if (displayFormatted) {
          // for the first check, make sure to check == null and not !unformattedValue
          // because 0s will be ignored otherwise
          const formatted = format(
            props,
            isNumber(unformattedValue == null ? '' : unformattedValue)
              ? toNumber(unformattedValue)
              : toNumber(lastValidValue)
          );
          let unformatted = unformatLocaleNumber(formatted, props.locale || '');
          if (zeroOnEmpty && !unformatted) {
            unformatted = 0;
          }

          setFocused(false);
          setUnformattedValue(unformatted?.toString());
          setFormattedValue(formatted);

          onBlur(event, toNumber(unformatted));
        } else {
          onBlur(event);
        }
      }
    },
    [onBlur, displayFormatted, unformattedValue, lastValidValue, zeroOnEmpty, props]
  );

  const onClickWrapper = useCallback(
    (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
      if (onClick) {
        onClick(event);
      }
    },
    [onClick]
  );

  const onFocusWrapper = useCallback(() => {
    if (displayFormatted) {
      setFocused(true);
    }
  }, [displayFormatted]);

  const inputClassNames = classNames(
    'ps-input',
    className,
    `ps-input--${type}`,
    disabled ? 'ps-input--disabled' : '',
    error ? 'ps-input--error' : ''
  );

  return (
    <input
      aria-label={props['aria-label']}
      autoComplete={autoComplete ? 'on' : 'off'}
      className={inputClassNames}
      defaultValue={defaultValue}
      disabled={disabled}
      id={id}
      min={min}
      max={max}
      maxLength={maxLength}
      name={name}
      placeholder={placeholder}
      role={role}
      readOnly={readOnly}
      ref={componentRef}
      type={displayFormatted ? 'text' : type}
      value={displayFormatted ? (focused ? unformattedValue : formattedValue) : value}
      onChange={onChangeWrapper}
      onKeyDown={onKeyDownWrapper}
      onBlur={onBlurWrapper}
      onClick={onClickWrapper}
      onFocus={onFocusWrapper}
    />
  );
});

Input.displayName = 'Input';
export { Input };
